/* Доменная модель Тренерграма.
   Иерархия: упражнение → (сет | связка) → блок → тренировка (день).
   Содержимое дня хранится в БД одним JSON-документом (WorkoutContent) —
   так его проще копировать, раздавать группе и версионировать
   (черновик и опубликованная версия — две ревизии). */

export type Id = string;

/** Тип блока (CON-18). null — обычный список подходов. */
export type BlockKind =
  | 'warmup'
  | 'strength'
  | 'complex'
  | 'cooldown'
  | 'stretch'
  | 'accessory'
  | 'gymnastics';

/** Формат комплекса (CON-18) — задаёт таймер клиента (TMR-1). Время в секундах. */
export type FormatKind = 'AMRAP' | 'EMOM' | 'FOR_TIME' | 'TABATA' | 'INTERVALS' | 'ROUNDS' | 'DEATH_BY' | 'NFT';

export interface ComplexFormat {
  k: FormatKind;
  rounds: number;
  work: number;
  rest: number;
  total: number;
  /** death by: стартовые повторы */
  start?: number;
}

/** Абсолютные единицы нагрузки (CON-19). Проценты — отдельный вид. */
export type AbsUnit = 'кг' | 'повт' | 'сек' | 'мин' | 'м' | 'кал';

/** Нагрузка: процент от ПМ или абсолютное значение; to — верхняя граница диапазона (CON-24). */
export type Load =
  | { kind: 'pct'; from: number; to?: number }
  | { kind: 'abs'; unit: AbsUnit; from: number; to?: number };

/** Строка упражнения. exId — из базы; raw — строка текстом (CON-5).
    txt — «как в тетради» при узнанном упражнении (CON-19); при txt схема и нагрузка пусты. */
/** Видео тренера (VID-1, VID-2): загружено в видеохостинг (Kinescope), здесь — ссылка для плеера */
export interface VideoRef {
  /** id в таблице videos */
  id: string;
  /** Адрес встраиваемого плеера */
  embed: string;
  title?: string;
}

/** Ссылка на видео с видеохостинга (VID-3): открывается на самом хостинге. host — подпись («YouTube», «Rutube»…) */
export interface VideoLink {
  url: string;
  host: string;
}

export interface ExLine {
  t: 'ex';
  id: Id;
  exId: Id | null;
  raw?: string | null;
  scheme?: string;
  load?: Load | null;
  txt?: string;
  /** Видео к строке текстом — разовое, ни к какому упражнению не привязано (копируется вместе с тренировкой).
      У упражнения из базы видео тренера берётся из базы само (Exercise.video). */
  video?: VideoRef | null;
  /** В этой строке убрано видео тренера из базы */
  noVideo?: boolean;
  /** В этой строке убрана демонстрация из общей базы (гифка) */
  noGif?: boolean;
  /** Ссылка на видео к строке текстом — как video, разовая */
  link?: VideoLink | null;
  /** В этой строке убрана ссылка тренера на видео из базы */
  noLink?: boolean;
}

export interface ChainPart {
  id: Id;
  exId: Id | null;
  raw?: string | null;
  /** повторы части */
  reps?: string;
}

/** Связка (CON-23): подходы и нагрузка — одни на всю связку. of — упражнение, от 1ПМ которого %, null — самое слабое. */
export interface ChainLine {
  t: 'chain';
  id: Id;
  parts: ChainPart[];
  /** подходы */
  sets?: number | null;
  /** связок в подходе: «3 подхода по 2 связки (1+1+1)» — 3×2×(1+1+1) */
  per?: number | null;
  load?: Load | null;
  of?: Id | null;
}

export type Line = ExLine | ChainLine;

/** Сет (CON-21): круги и отдых между кругами; нагрузка участников — на один круг. */
export interface SetGroup {
  t: 'set';
  id: Id;
  rounds: number;
  rest?: string;
  items: Line[];
}

export type BlockItem = Line | SetGroup;

export interface Block {
  id: Id;
  kind: BlockKind | null;
  title: string;
  /** Заметка к блоку — видит клиент (COM-4) */
  note: string;
  /** Только у комплекса */
  fmt: ComplexFormat | null;
  /** Блок текстом (CON-5): если строка задана — items пуст */
  text?: string | null;
  items: BlockItem[];
}

export interface WorkoutContent {
  title: string;
  /** Соревнование (CAL-2) */
  comp?: boolean;
  /** Сообщение тренера ко всей тренировке (COM-4) */
  message?: string;
  blocks: Block[];
}

export type DayStatus = 'rest' | 'draft' | 'published' | 'comp';

/** Упражнение базы (EX-1). pm — общий ключ 1ПМ вариаций одного движения. */
export interface Exercise {
  id: Id;
  ru: string;
  en: string;
  group: string;
  equipment: string;
  bodyPart?: string;
  target?: string;
  secondary?: string[];
  pm?: string | null;
  units: AbsUnit[];
  aliases?: string[];
  own?: boolean;
  /** Шаг округления рабочего веса, кг (по умолчанию 2,5) */
  step?: number;
  gif?: string | null;
  img?: string | null;
  /** Видео тренера к упражнению (VID-2): своё у каждого тренера, другим не видно */
  video?: VideoRef | null;
  /** Ссылка тренера на видео к упражнению (VID-3): как video — своя у каждого тренера */
  link?: VideoLink | null;
}

/** Максимумы клиента: ключ 1ПМ → кг */
export type Maxes = Record<string, number>;

export const LEVELS = ['Новичок', 'Есть опыт', 'Продвинутый', 'Спортсмен'] as const;
export const GOALS = [
  'Похудеть',
  'Набрать мышечную массу',
  'Стать сильнее',
  'Выносливость',
  'Здоровье и самочувствие',
  'Подготовка к соревнованиям',
] as const;
